import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import * as d3 from 'd3';

interface Tag {
  name: string;
  count: number;
}

interface TagCloudProps {
  tags: Tag[];
  onTagSelect: (tag: string) => void;
  selectedTags: string[];
  personas?: any[]; // Add personas to calculate tag relationships
}

export const TagCloud: React.FC<TagCloudProps> = ({
  tags,
  onTagSelect,
  selectedTags,
  personas = []
}) => {
  const { darkMode } = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tagRelations, setTagRelations] = useState<{source: string, target: string, strength: number}[]>([]);

  // Calculate relationships between tags
  useEffect(() => {
    if (!personas || personas.length === 0 || !tags || tags.length === 0) {
      console.log('TagCloud: Missing data for calculating relationships', { 
        personasLength: personas?.length || 0, 
        tagsLength: tags?.length || 0 
      });
      return;
    }
    
    console.log('TagCloud: Calculating relationships for', { 
      personasCount: personas.length, 
      tagsCount: tags.length 
    });
    
    // Create a map to track co-occurrences of tags
    const coOccurrences: Record<string, Record<string, number>> = {};
    
    // Initialize the map for all tags
    tags.forEach(tag => {
      coOccurrences[tag.name] = {};
    });
    
    // Count co-occurrences in personas
    let validPersonasCount = 0;
    personas.forEach((persona, index) => {
      if (!persona.tags || persona.tags.length < 2) {
        console.log(`TagCloud: Persona ${index} has insufficient tags:`, persona.tags);
        return;
      }
      
      validPersonasCount++;
      // For each pair of tags in this persona
      for (let i = 0; i < persona.tags.length; i++) {
        for (let j = i + 1; j < persona.tags.length; j++) {
          const tag1 = persona.tags[i];
          const tag2 = persona.tags[j];
          
          // Skip if either tag isn't in our tags list
          if (!coOccurrences[tag1] || !coOccurrences[tag2]) {
            console.log(`TagCloud: Tag pair not in coOccurrences map:`, { tag1, tag2 });
            continue;
          }
          
          // Increment co-occurrence count
          coOccurrences[tag1][tag2] = (coOccurrences[tag1][tag2] || 0) + 1;
          coOccurrences[tag2][tag1] = (coOccurrences[tag2][tag1] || 0) + 1;
        }
      }
    });
    
    console.log(`TagCloud: Found ${validPersonasCount} valid personas with multiple tags`);
    
    // Convert all co-occurrences to links without a threshold
    const links: {source: string, target: string, strength: number}[] = [];
    
    let totalPotentialLinks = 0;
    
    Object.keys(coOccurrences).forEach(source => {
      Object.keys(coOccurrences[source]).forEach(target => {
        if (source < target) { // Avoid counting pairs twice
          totalPotentialLinks++;
          const count = coOccurrences[source][target];
          // Include all connections with a strength > 0
          if (count > 0) {
            links.push({
              source,
              target,
              strength: count
            });
          }
        }
      });
    });
    
    console.log(`TagCloud: Found ${links.length} links out of ${totalPotentialLinks} potential links`);
    
    // Sort by strength and take top N links to avoid cluttering
    // We'll take more links now to ensure a well-connected graph
    links.sort((a, b) => b.strength - a.strength);
    const maxLinks = Math.min(links.length, Math.max(150, tags.length * 2)); // Take more links for a heavier graph
    const finalLinks = links.slice(0, maxLinks);
    console.log(`TagCloud: Final links for visualization:`, finalLinks);
    
    setTagRelations(finalLinks);
  }, [personas, tags]);

  useEffect(() => {
    if (!tags.length || !svgRef.current || !containerRef.current) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Get container dimensions
    const width = containerRef.current.clientWidth;
    const height = 500; // Fixed height or you can make it responsive too

    // Create the SVG
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // Create nodes from tags - MAKE NODES SMALLER
    const nodes = tags.map(tag => ({
      id: tag.name,
      radius: Math.max(12, Math.min(30, 10 + tag.count * 1.5)), // Smaller radius
      count: tag.count,
      selected: selectedTags.includes(tag.name)
    }));

    // Create links from tag relations
    const links = tagRelations.map(relation => {
      // Validate that source and target exist in nodes
      const sourceExists = nodes.some(node => node.id === relation.source);
      const targetExists = nodes.some(node => node.id === relation.target);
      
      if (!sourceExists || !targetExists) {
        console.warn('TagCloud: Link references non-existent node', {
          relation,
          sourceExists,
          targetExists
        });
      }
      
      return {
        source: relation.source,
        target: relation.target,
        value: relation.strength * 2 // Increase link strength for heavier graph
      };
    }).filter(link => {
      // Filter out links with non-existent nodes
      const sourceExists = nodes.some(node => node.id === link.source);
      const targetExists = nodes.some(node => node.id === link.target);
      return sourceExists && targetExists;
    });
    
    console.log('TagCloud: Created links array with', links.length, 'valid links');

    console.log('TagCloud: Creating visualization with', { 
      nodes: nodes.length, 
      links: links.length,
      containerWidth: width,
      containerHeight: height
    });
    
    // Create shadow filters for nodes
    const defs = svg.append('defs');
    
    // Filter for normal nodes
    const filterNormal = defs.append('filter')
      .attr('id', 'shadow-normal')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
      
    filterNormal.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '1')
      .attr('stdDeviation', '2')
      .attr('flood-opacity', darkMode ? '0.3' : '0.2')
      .attr('flood-color', darkMode ? '#000' : '#888');
      
    // Filter for selected nodes with stronger shadow
    const filterSelected = defs.append('filter')
      .attr('id', 'shadow-selected')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
      
    filterSelected.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '2')
      .attr('stdDeviation', '3')
      .attr('flood-opacity', '0.4')
      .attr('flood-color', darkMode ? '#1e40af' : '#3b82f6');

    // Create force simulation - MAKE CONNECTIONS SHORTER
    const simulation = d3.forceSimulation(nodes)
      .force('charge', d3.forceManyBody().strength(-150)) // Reduce repulsion for shorter connections
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => (d as any).radius + 5)) // Reduce collision padding
      .force('link', d3.forceLink(links).id(d => (d as any).id).strength(0.5)) // Increase link strength to pull nodes closer
      .velocityDecay(0.4) // Higher velocity decay for slower movement (default is 0.4)
      .alphaDecay(0.01) // Lower alpha decay for longer simulation (default is 0.0228)
      .on('tick', ticked);
    
    console.log('TagCloud: Force simulation created with forces:', {
      charge: -150,
      collision: true,
      link: links.length > 0 ? 'enabled' : 'no links',
      velocityDecay: 0.4,
      alphaDecay: 0.01
    });

    // Create links with thicker lines
    const link = svg.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', darkMode ? '#4b5563' : '#d1d5db')
      .attr('stroke-opacity', 0.7) // More visible
      .attr('stroke-width', d => Math.sqrt(d.value) + 1); // Thicker lines
    
    console.log(`TagCloud: Rendered ${link.size()} link elements`);

    // Create node elements
    const node = svg.append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'tag-node')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        onTagSelect(d.id);
      })
      .call(d3.drag<SVGGElement, any>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended)
      );

    // Add circles to nodes with shadow effect
    node.append('circle')
      .attr('r', d => d.radius)
      .attr('fill', d => {
        if (d.selected) {
          return darkMode ? '#3b82f6' : '#3b82f6'; // Blue for selected
        }
        return darkMode ? '#374151' : '#e5e7eb'; // Gray for unselected
      })
      .attr('stroke', d => {
        if (d.selected) {
          return darkMode ? '#93c5fd' : '#1e40af'; // Border for selected
        }
        return darkMode ? '#4b5563' : '#d1d5db'; // Border for unselected
      })
      .attr('stroke-width', 2)
      .attr('filter', d => d.selected ? 'url(#shadow-selected)' : 'url(#shadow-normal)');

    // Add text to nodes - smaller font
    node.append('text')
      .text(d => d.id)
      .attr('text-anchor', 'middle')
      .attr('dy', '.3em')
      .attr('font-size', d => Math.min(d.radius * 0.7, 10)) // Smaller font
      .attr('fill', d => {
        if (d.selected) {
          return darkMode ? 'white' : 'white';
        }
        return darkMode ? '#e5e7eb' : '#1f2937';
      });

    // Add count badges - smaller
    node.append('text')
      .text(d => d.count)
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.radius + 10) // Closer to the node
      .attr('font-size', '8px') // Smaller font
      .attr('fill', darkMode ? '#9ca3af' : '#6b7280');

    // Tick function to update positions
    function ticked() {
      // Debug the first few ticks
      const tickCount = (simulation as any)._ticks || 0;
      (simulation as any)._ticks = tickCount + 1;
      
      if (tickCount < 5 || tickCount % 20 === 0) {
        console.log(`TagCloud: Tick ${tickCount}, alpha: ${simulation.alpha().toFixed(3)}`);
      }
      
      // Update link positions
      link
        .attr('x1', d => {
          if (!d.source || typeof d.source === 'string' || !('x' in d.source)) {
            console.warn('TagCloud: Invalid source in link', d);
            return 0;
          }
          return Math.max(0, Math.min(width, (d.source as any).x));
        })
        .attr('y1', d => {
          if (!d.source || typeof d.source === 'string' || !('y' in d.source)) {
            return 0;
          }
          return Math.max(0, Math.min(height, (d.source as any).y));
        })
        .attr('x2', d => {
          if (!d.target || typeof d.target === 'string' || !('x' in d.target)) {
            console.warn('TagCloud: Invalid target in link', d);
            return 0;
          }
          return Math.max(0, Math.min(width, (d.target as any).x));
        })
        .attr('y2', d => {
          if (!d.target || typeof d.target === 'string' || !('y' in d.target)) {
            return 0;
          }
          return Math.max(0, Math.min(height, (d.target as any).y));
        });

      // Update node positions
      node.attr('transform', d => {
        const x = Math.max(d.radius, Math.min(width - d.radius, d.x));
        const y = Math.max(d.radius, Math.min(height - d.radius, d.y));
        return `translate(${x},${y})`;
      });
    }

    // Drag functions
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      svg.attr('width', newWidth);
      simulation.force('center', d3.forceCenter(newWidth / 2, height / 2));
      simulation.alpha(0.3).restart();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      simulation.stop();
    };
  }, [tags, selectedTags, darkMode, onTagSelect, tagRelations]);

  return (
    <div 
      ref={containerRef} 
      className={`tag-cloud-container w-full rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } p-4 transition-colors duration-200`}
      style={{ aspectRatio: '1/1' }}
    >
      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
        Drag nodes to rearrange. Connected tags appear together in personas.
      </p>
      <svg ref={svgRef} style={{ width: '100%', height: 'calc(100% - 20px)' }}></svg>
    </div>
  );
};
